import showLoginPopup from '../../_utils/login-launcher';
import { WebUI, builderDispatch } from '../../_utils/ui';

// The builder's mutating endpoints refuse a caller who cannot prove the project is theirs
// (see ProjectEditAccessControl). The common way to hit this is honest: a customer built the
// project while logged in, then reopened it in another window or on another device without
// logging in. The server answers 403 with a reason saying whether logging in could help.
//
// Callers previously ignored the response entirely, so a refusal was invisible: the UI showed the
// new value while the database kept the old one, and the change only appeared to be lost on the
// next reload.
//
// Exported so recheck-access-on-focus.js can compare against NOT_LOGGED_IN directly, instead of
// hardcoding the same string a second time.
export const NOT_LOGGED_IN = 'not-logged-in';
const NOT_AUTHORISED = 'not-authorised';

const HOME = '/';

// A blocked or broken login window would otherwise leave the prompt cycling forever. Enough
// attempts to cover a mistyped password, then a way out rather than a trap.
const MAX_LOGIN_ATTEMPTS = 5;

// Read the refusal without assuming a JSON body — a proxy or an unrelated 403 may return HTML.
export const readRefusal = async (response) => {
  try {
    const body = await response.clone().json();
    return {
      reason: body?.error ?? null,
      freshStartUrl: body?.fresh_start_url ?? null,
    };
  } catch {
    return { reason: null, freshStartUrl: null };
  }
};

// ConfirmationDialog is mounted by both builders and listens for this dispatch. Resolving on the
// button press keeps the caller's control flow linear, and every dismissal route - the cancel
// button, the close control, the backdrop - lands on onCancel.
export const showLoginPrompt = ({ again = false, canStartFresh = true } = {}) =>
  new Promise((resolve) => {
    builderDispatch.dispatch({
      type: WebUI.SHOW_CONFIRMATION_DIALOG,
      title: again
        ? 'Log in to keep this change'
        : 'Log in to open this project',
      // Says whose the project is and what each choice does, rather than dwelling on the failure.
      // The second time round they have just come back from a login they did not finish, so it
      // names the two ways forward instead of repeating what they now know.
      message: again
        ? 'This project is still saved to the account that created it. Log in to keep this ' +
          'change, or start a new project of your own from the same design.'
        : 'Projects are saved to the account that created them. Log in to view and edit this ' +
          'one, or start a new project of your own from the same design.',
      applyButtonText: 'Log in',
      // Without a design to seed a new project from there is nothing to offer, so the second
      // choice goes back to simply leaving.
      cancelButtonText: canStartFresh ? 'Start a new project' : 'Not now',
      variantClassName: 'access-refused-dialog',
      // "Log in" and "Start a new project" go different places, so a click on the background or
      // an escape keypress must not silently pick one.
      requireChoice: true,
      onConfirm: () => resolve(true),
      onCancel: () => resolve(false),
    });
  });

const showRejectedNotice = () =>
  new Promise((resolve) => {
    builderDispatch.dispatch({
      type: WebUI.SHOW_CONFIRMATION_DIALOG,
      title: 'This project is on another account',
      // Most people who see this logged into the wrong one of their own accounts, so it reads as
      // a mix-up to correct rather than an accusation, and names the way out.
      message:
        'This project belongs to a different account, so you cannot make changes to it while ' +
        'logged in as you are. Log out and back in with the account that created it to pick up ' +
        'where you left off.',
      applyButtonText: 'OK',
      hideCancel: true,
      variantClassName: 'access-refused-dialog',
      onConfirm: () => resolve(true),
      onCancel: () => resolve(true),
    });
  });

// A latch on WebUI.LOGIN. `settled()` can be polled without consuming the event, so the prompt
// loop can ask "have they logged in yet?" between showings.
const waitForLogin = () => {
  let done = false;
  let token = null;

  const promise = new Promise((resolve) => {
    token = builderDispatch.register((payload) => {
      if (payload.type !== WebUI.LOGIN) return;

      done = true;
      resolve();
    });
  });

  return {
    promise,
    settled: () => done,
    cancel: () => {
      if (token !== null) builderDispatch.unregister(token);
      token = null;
    },
  };
};

// How long to wait for a login window that never reports back before asking again. A blocked
// popup never takes focus, so the `focus` arm below would wait forever on its own.
const POPUP_SILENCE_TIMEOUT = 60000;

// Focus returning to this document is the reliable signal that the customer is looking at the
// builder again - whether they logged in, closed the window, or gave up on it. But `focus` only
// fires if the document lost focus first, which a blocked popup never causes, so a timeout runs
// alongside it to keep the loop moving.
const popupClosed = () =>
  new Promise((resolve) => {
    const onFocus = () => {
      window.removeEventListener('focus', onFocus);
      clearTimeout(timer);
      // Let a successful login's postMessage land first, so a win is not mistaken for a close.
      setTimeout(resolve, 400);
    };

    // Whichever arrives first wins; both paths drop the focus listener before resolving.
    const timer = setTimeout(() => {
      window.removeEventListener('focus', onFocus);
      resolve();
    }, POPUP_SILENCE_TIMEOUT);

    window.addEventListener('focus', onFocus);
  });

/**
 * Handle a refused builder mutation.
 *
 * Resolves to `true` when the response was a refusal this handled, so a caller can skip its own
 * success path. Anything else (including a 403 without a recognised reason) resolves to `false`
 * and is left to the caller.
 *
 * @param {Response} response          the fetch Response to inspect
 * @param {Function} [options.onRetry] re-runs the change once the customer has logged in
 * @param {Function} [options.onPrompt] renders the login prompt; resolves true to log in,
 *                                       false to leave. Injectable so the decision can be tested
 *                                       without a DOM; defaults to the builder's own dialog.
 * @param {Function} [options.onRejected] renders the wrong-account notice
 * @param {Function} [options.onAwaitLogin] resolves when the customer is back from the login
 *                                          window still logged out, prompting another ask
 */
export const handleRefusedChange = async (
  response,
  {
    onRetry,
    onPrompt = showLoginPrompt,
    onRejected = showRejectedNotice,
    onAwaitLogin = popupClosed,
  } = {},
) => {
  if (!response || response.status !== 403) return false;

  const { reason, freshStartUrl } = await readRefusal(response);

  // Logging in cannot make someone else's project theirs, so say so rather than offering a login
  // that would only refuse them again.
  if (reason === NOT_AUTHORISED) {
    await onRejected();
    window.location.assign(HOME);
    return true;
  }

  if (reason !== NOT_LOGGED_IN) return false;

  // There are only two ways out of this state: logged in, or off the page. Closing the login
  // window without logging in would otherwise drop them back onto a project that still cannot
  // save, with nothing on screen saying so - the same silent failure this exists to remove. So
  // the prompt comes back until one of those two things is true.
  //
  // The popup reports success through `window.onLoggedIn`, which updates App.order and dispatches
  // WebUI.LOGIN (see app/views/login/postlogin.html.slim). By then `postlogin` has run
  // HandleUserLogin, which adopts orphaned projects and calls autocreate_order, so the session
  // owns the order and the retry succeeds. There is no matching signal for "closed the window
  // without logging in", which is exactly why re-prompting has to be the default rather than
  // something we do on a cancel event.
  const loggedIn = waitForLogin();

  // Ask, open the login window, and ask again if they come back still logged out. The only exits
  // are logging in or choosing to leave.
  for (let attempt = 0; attempt < MAX_LOGIN_ATTEMPTS; attempt += 1) {
    // Checked before asking as well as after: a login that lands while the window is open ends
    // this, and asking again first would flash a redundant dialog over a session that is now fine.
    if (loggedIn.settled()) break;

    const wantsToLogIn = await onPrompt({
      again: attempt > 0,
      canStartFresh: !!freshStartUrl,
    });

    if (loggedIn.settled()) break;

    if (!wantsToLogIn) {
      loggedIn.cancel();
      // A new project of their own, on the same design, rather than being turned out to the
      // homepage. The original is untouched and nothing is carried across.
      window.location.assign(freshStartUrl || HOME);
      return true;
    }

    const popup = showLoginPopup();

    // A popup the browser refused to open never takes focus and never reports a login, so there
    // is nothing to wait for: break out and let the escape hatch below send them somewhere that
    // can log them in properly.
    if (!popup || popup.closed) {
      break;
    }

    // Whichever happens first: they log in, or they come back to the builder still logged out
    // and we ask again.
    await Promise.race([loggedIn.promise, onAwaitLogin()]);
  }

  const succeeded = loggedIn.settled();

  loggedIn.cancel();

  // Not settled means the login signal never arrived - a blocked popup, or a login window that
  // reports back in a way this page never hears. Take them off a project they cannot save to:
  // a new project of their own if there is one to offer, otherwise home. Neither logs them in,
  // but both beat re-asking a question that will never be answered.
  if (succeeded) {
    onRetry?.();
  } else {
    window.location.assign(freshStartUrl || HOME);
  }

  return true;
};

export default handleRefusedChange;
